<template>
  <admin-wrapper
    ref="form"
    :straight-title="additionalModuleName"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    :inputs="inputs"
    @save="save"
  >
    <template v-for="(input, idx) of inputs" #[`${input.name}.input`]="data">
      <v-col cols="3" :key="idx" v-if="data.datasource">
        <data-input
          v-model="selectedDataSources[`masterplan_${data.datasource}_datasource`].datasource"
          v-bind="data"
          @change="clearDataSourceColumns(data.datasource)"
        />
      </v-col>
      <v-col :key="idx" cols="12" class="text-left pb-4" v-else-if="data.header">
        <card-title-row v-bind="data" />
      </v-col>
      <template v-else-if="data.datasourceAttribute">
        <v-col cols="3" :key="idx" class="px-1">
          <data-input
            v-bind="data"
            v-model="selectedDataSources[`masterplan_${data.datasourceAttribute}_datasource`].settings[data.name]"
            @change="selectedDataSources[`masterplan_${data.datasourceAttribute}_datasource`].changed = true"
          />
        </v-col>
        <template v-for="(value, aidx) of data.attributeValues">
          <v-col cols="2" :key="`${idx}-${aidx}`" class="px-1" align-self="center">
            <data-input
              v-bind="value"
              v-model="selectedDataSources[`masterplan_${data.datasourceAttribute}_datasource`].settings[value.name]"
              @change="selectedDataSources[`masterplan_${data.datasourceAttribute}_datasource`].changed = true"
            />
          </v-col>
        </template>
      </template>
    </template>
  </admin-wrapper>
</template>

<script>
import { call, get } from 'vuex-pathify';

import AdminWrapper from '@/components/AdminWrapper';
import CardTitleRow from '@/components/CardTitleRow';
import DataInput from '@/components/DataInput';

import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';

export default {
  name: 'MasterplanStreetsTwoSettings',
  components: { AdminWrapper, CardTitleRow, DataInput },
  mixins: [prepareAttributesFeatureCardMixin],
  computed: {
    additionalModuleName: get('admin/additionalModules@MASTERPLAN_2_STREETS.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@masterplan_2_streets_module_enabled'),
    moduleEnabler() {
      return {
        name: 'masterplan_2_streets_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle2', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'streetsDataSource',
          datasource: 'street',
          translationPath: 'admin.masterplanStreets.streetsDataSource',
          items: this.dataSourcesCoreLineGeometryMapped,
          ...this.dataSourceDefaultProps,
        },
        {
          name: 'situationsDataSource',
          datasource: 'situation',
          translationPath: 'admin.masterplanStreets.situationsDataSource',
          items: this.dataSourcesCoreNonGeometryMapped,
          ...this.dataSourceDefaultProps,
        },
        {
          name: 'streetsDataSourceCalculatorHeader',
          translationPath: `admin.masterplanStreets.headers.streetsDataSourceCalculatorHeader`,
          ...this.headerDefaultProps,
        },
        ...this.streetsDataSourceAttributes.map(attribute => {
          return {
            name: attribute.name,
            datasourceAttribute: 'street',
            translationPath: `admin.masterplanStreets.attributes.${attribute.name}`,
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: { name: 'select' },
            items: this.selectedStreetsDataSourceAttributes.filter(dSAttribute =>
              attribute.type.includes(dSAttribute.dataType?.name)
            ),
            disabled: !this.selectedDataSources['masterplan_street_datasource'].datasource,
          };
        }),
        {
          name: 'situationsDataSourceCalculatorHeader',
          translationPath: `admin.masterplanStreets.headers.situationsDataSourceCalculator`,
          ...this.headerDefaultProps,
        },
        ...this.situationsDataSourceAttributes.map(attribute => {
          return {
            name: attribute.name,
            datasourceAttribute: 'situation',
            translationPath: `admin.masterplanStreets.attributes.${attribute.name}`,
            rules: [v => !!v || v == '0' || 'rules.required'],
            dataType: {
              name: 'select',
            },
            items: this.selectedSituationsDataSourceAttributes.filter(dSAttribute =>
              attribute.type.includes(dSAttribute.dataType?.name)
            ),
            disabled: !this.selectedDataSources['masterplan_situation_datasource'].datasource,
            ...(attribute.attributeValues && {
              attributeValues: attribute.attributeValues.map(value => {
                return {
                  name: value.name,
                  label: this.$i18n.t(`admin.masterplanStreets.attributes.${value.name}`),
                  rules: [v => !!v || v == '0' || 'rules.required'],
                  dataType: { name: 'select' },
                  items: this.getAttributeValues(
                    this.selectedDataSources['masterplan_situation_datasource'].datasource,
                    this.selectedDataSources['masterplan_situation_datasource'].settings[attribute.name]
                  ),
                  disabled:
                    !this.selectedDataSources['masterplan_situation_datasource'].datasource ||
                    !this.selectedDataSources['masterplan_situation_datasource'].settings[attribute.name],
                };
              }),
            }),
          };
        }),
      ];
    },

    dataSourcesCoreLineGeometryMapped() {
      return this.$_objectToArray(this.dataSources)
        .filter(dS => dS.scope === 'core' && dS.geometry_type?.includes('line'))
        .map(dS => {
          const { name, verbose_name } = dS;
          return { value: name, text: verbose_name };
        });
    },
    dataSourcesCoreNonGeometryMapped() {
      return this.$_objectToArray(this.dataSources)
        .filter(dS => dS.scope === 'core' && !dS.geometry_type)
        .map(dS => {
          const { name, verbose_name } = dS;
          return { value: name, text: verbose_name };
        });
    },

    dataSources: get('layers/metadata'),
    selectedSituationsDataSourceName() {
      return this.selectedDataSources.masterplan_situation_datasource.datasource || '';
    },
    selectedSituationsDataSourceMetadata() {
      return this.dataSources?.[this.selectedSituationsDataSourceName] || undefined;
    },
    selectedSituationsDataSourceAttributesSchema() {
      return this.selectedSituationsDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedSituationsDataSourceAttributes() {
      return this.selectedSituationsDataSourceAttributesSchema
        .filter(attribute => !attribute.read_only)
        .map(attribute => {
          const { name: value, verbose_name: text, data_type: dataType } = attribute;
          return { value, text, dataType };
        });
    },
    selectedStreetsDataSourceName() {
      return this.selectedDataSources.masterplan_street_datasource.datasource || '';
    },
    selectedStreetsDataSourceMetadata() {
      return this.dataSources?.[this.selectedStreetsDataSourceName] || undefined;
    },
    selectedStreetsDataSourceAttributesSchema() {
      return this.selectedStreetsDataSourceMetadata?.attributes_schema?.attributes || [];
    },
    selectedStreetsDataSourceAttributes() {
      return this.selectedStreetsDataSourceAttributesSchema
        .filter(attribute => !attribute.read_only)
        .map(attribute => {
          const { name: value, verbose_name: text, data_type: dataType } = attribute;
          return { value, text, dataType };
        });
    },
  },
  data: () => ({
    headerDefaultProps: {
      hasIconColumn: false,
      isNamedSlot: false,
      customSize: 'text-subtitle-3',
      titleClasses: ['font-weight-medium'],
      header: true,
    },
    dataSourceDefaultProps: {
      rules: [v => !!v || v == '0' || 'rules.required'],
      dataType: {
        name: 'select',
      },
    },
    isFormFetched: false,
    loading: false,
    streetsDataSourceAttributes: [
      {
        name: 'mount_height_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'overhang_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'road_distance_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'pick_arm_length_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'tilt_angle_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'poles_distance_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'arrangement_attribute',
        type: ['text', 'integer'],
      },
    ],
    situationsDataSourceAttributes: [
      {
        name: 'type_attribute',
        type: ['text'],
        attributeValues: [
          {
            name: 'type_lane_value',
          },
          {
            name: 'type_green_strip_value',
          },
          {
            name: 'type_median_strip_value',
          },
        ],
      },
      {
        name: 'street_id_attribute',
        type: ['integer'],
      },
      {
        name: 'order_attribute',
        type: ['integer'],
      },
      {
        name: 'width_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'situation_attribute',
        type: ['text'],
      },
      {
        name: 'class_attribute',
        type: ['integer'],
      },
      {
        name: 'calculation_attribute',
        type: ['text'],
      },
      {
        name: 'lanes_attribute',
        type: ['integer', 'decimal', 'float'],
      },
      {
        name: 'surface_attribute',
        type: ['text'],
      },
      {
        name: 'reflectivity_attribute',
        type: ['text'],
      },
      {
        name: 'brightness_attribute',
        type: ['integer', 'decimal', 'float'],
      },
    ],
    selectedDataSources: {
      masterplan_street_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
      masterplan_situation_datasource: {
        changed: false,
        datasource: '',
        settings: {},
      },
    },
  }),
  methods: {
    editDatasourceSettings: call('datasourceSettings/editDatasourceSettings'),
    getDataSources: call('layers/getLayersMetadata'),
    getDatasourceSettings: call('datasourceSettings/getDatasourceSettings'),
    getAttributeValues(datasource, attribute) {
      return (
        this.dataSources[datasource]?.attributes_schema.attributes.find(a => a.name === attribute)?.allowed_values || []
      );
    },
    clearDataSourceColumns(dataSourceType) {
      this.selectedDataSources[`masterplan_${dataSourceType}_datasource`].settings = {
        ...this[`${dataSourceType}sDataSourceAttributes`]?.reduce((total, current) => {
          total[current.name] = '';
          return total;
        }, {}),
      };
      this.selectedDataSources[`masterplan_${dataSourceType}_datasource`].changed = true;
      this.$refs.form?.$refs.form?.$refs.form?.resetValidation();
    },
    async setSettingsValues() {
      if (!this.isModuleEnabled) return;
      const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
      const promises = selectedDataSourcesKeys.map(selectedDataSourcesKey => {
        return this.getDatasourceSettings(selectedDataSourcesKey);
      });
      const rPromisesData = await Promise.all(promises);
      this.selectedDataSources = rPromisesData.reduce(
        (total, current, index) => ({
          ...total,
          ...{
            [selectedDataSourcesKeys[index]]: {
              datasource: current?.datasource || '',
              settings: current?.settings || {},
              changed: false,
            },
          },
        }),
        {}
      );
    },
    async init() {
      await Promise.all([this.getDataSources({ with_attributes_schema: true }), this.setSettingsValues()]);
      this.isFormFetched = true;
      this.$nextTick(() => {
        this.$refs.form?.$refs.form?.$refs.form?.resetValidation();
      });
    },
    async save() {
      try {
        this.loading = true;
        const selectedDataSourcesKeys = Object.keys(this.selectedDataSources);
        const filteredSelectedDataSourcesKeys = selectedDataSourcesKeys.filter(
          key => this.selectedDataSources[key].changed
        );
        const promises = filteredSelectedDataSourcesKeys.map(selectedDataSourcesKey => {
          const settings = JSON.parse(JSON.stringify(this.selectedDataSources[selectedDataSourcesKey]));
          delete settings.changed;
          const payload = { name: selectedDataSourcesKey, settings };
          return this.editDatasourceSettings(payload);
        });
        await Promise.all(promises);
        this.selectedDataSources = selectedDataSourcesKeys.reduce(
          (total, current) => ({ ...total, [current]: { ...this.selectedDataSources[current], changed: false } }),
          {}
        );
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.successEditSetting'),
        });
      } finally {
        this.loading = true;
      }
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.isFormFetched = false;
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
